import { LoadingButton } from '@mui/lab';
import { BASIC_ORDER_HISTORY_STATUSES } from 'common/constants/orderStatusGroups';
import useInfoDialog from 'common/hooks/useInfoDialog';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  getPatientOrderHistory,
  listPatientOrders,
  sendOrderBackToQueue,
} from 'store/thunks/patientThunks';
import BatchRollbackDialog from './BatchRollbackDialog';

const SendOrderBackToQueueButton = ({
  mpi,
  orderId,
  disabled,
  onSuccess,
  isInBatch,
  batchShipmentId,
  isInShipment,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const { InfoDialog, showInfoDialog } = useInfoDialog();

  const handleSuccess = (orderIds, skippedOrders = []) => {
    const skippedOrderIds = new Set(skippedOrders.map((skipped) => skipped.orderId));
    const processedCount = orderIds.filter((id) => !skippedOrderIds.has(id)).length;

    if (processedCount > 0) {
      enqueueSnackbar(
        processedCount > 1
          ? `${processedCount} orders sent back to queue successfully`
          : 'Order sent back to queue successfully',
        { variant: 'success' }
      );
    }

    if (skippedOrders.length > 0) {
      enqueueSnackbar(
        `Skipped ${skippedOrders.length} order${skippedOrders.length > 1 ? 's' : ''}: ${skippedOrders
          .map((skipped) => `${skipped.orderId} (${skipped.reason})`)
          .join(', ')}`,
        { variant: 'warning' }
      );
    }

    onSuccess?.();
    dispatch(listPatientOrders({ mpi }));
    dispatch(
      getPatientOrderHistory({
        mpi,
        status: BASIC_ORDER_HISTORY_STATUSES,
        includeAllFacilities: true,
      })
    );
  };

  const handleClick = () => {
    if (isInBatch && isInShipment) {
      setBatchDialogOpen(true);
      return;
    }
    const batchWarning = isInBatch
      ? ' Note: this will only roll back this order — it will not roll back the entire batch. To roll back the full batch, go to the Batch Shipments screen.'
      : '';
    showInfoDialog({
      title: 'Send Order Back To Queue',
      message: `Are you sure you want to send this order back to the queue?${batchWarning}`,
      isWarning: true,
      callback: () => {
        setIsLoading(true);
        return dispatch(sendOrderBackToQueue({ orderIds: [orderId], rollbackType: 'SINGLE' }))
          .then((response) => {
            if (response?.success) {
              handleSuccess([orderId], response.skippedOrders);
            }
          })
          .finally(() => setIsLoading(false));
      },
      cancelable: true,
      confirmButtonText: 'Yes',
    });
  };

  const handleBatchConfirm = (selectedOrderIds) => {
    return dispatch(
      sendOrderBackToQueue({ orderIds: selectedOrderIds, rollbackType: 'SINGLE' })
    ).then((response) => {
      if (response?.success) {
        setBatchDialogOpen(false);
        handleSuccess(selectedOrderIds, response.skippedOrders);
      }
    });
  };

  // `disabled` here is only ever an eligibility answer about the order's status, never a transient
  // busy state - that is `isLoading`, which drives the button's own spinner. So the button is left
  // out entirely rather than shown permanently greyed on an order that can never use it.
  if (disabled) return null;

  return (
    <>
      <InfoDialog />
      <BatchRollbackDialog
        open={batchDialogOpen}
        onClose={() => setBatchDialogOpen(false)}
        onConfirm={handleBatchConfirm}
        batchShipmentId={batchShipmentId}
        currentOrderId={orderId}
      />
      <LoadingButton
        size='small'
        variant='outlined'
        loading={isLoading}
        onClick={handleClick}
        sx={{
          px: 1,
          fontSize: '0.75rem',
          minHeight: '30px',
          lineHeight: 'normal',
        }}
      >
        Send Order Back To Queue
      </LoadingButton>
    </>
  );
};

SendOrderBackToQueueButton.propTypes = {
  mpi: PropTypes.string.isRequired,
  orderId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onSuccess: PropTypes.func,
  isInBatch: PropTypes.bool,
  batchShipmentId: PropTypes.string,
  isInShipment: PropTypes.bool,
};

SendOrderBackToQueueButton.defaultProps = {
  disabled: false,
  onSuccess: undefined,
  isInBatch: false,
  batchShipmentId: undefined,
  isInShipment: false,
};

export default SendOrderBackToQueueButton;
